import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

// Direkta REST-anrop mot GoTrue OAuth-endpoints. `supabase.auth.oauth`-namespacet
// saknas i installerad supabase-js (2.58) så vi kan inte använda beta-helpers.
const AUTH_BASE = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

async function authFetch(path: string, init: RequestInit = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(`${AUTH_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return { raw: text }; } })() : {};
  if (!res.ok) {
    return { data: null, error: { message: body?.error_description || body?.msg || body?.error || `HTTP ${res.status}` } };
  }
  return { data: body, error: null };
}

interface AuthorizationDetails {
  client?: { name?: string; client_uri?: string };
  redirect_uri?: string;
  scopes?: string[];
  redirect_url?: string;
  redirect_to?: string;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [sessionReady, setSessionReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setSignedIn(!!data.session);
      setSessionReady(true);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(!!s));
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!sessionReady || !signedIn) return;
    if (!authorizationId) { setError("Saknar authorization_id."); return; }
    let active = true;
    (async () => {
      try {
        const { data, error } = await authFetch(`/oauth/authorizations/${encodeURIComponent(authorizationId)}`);
        if (!active) return;
        if (error) return setError(error.message ?? "Kunde inte läsa auktorisationen.");
        const immediate = data?.redirect_url ?? data?.redirect_to;
        if (immediate && !data?.client) { window.location.href = immediate; return; }
        setDetails(data);
      } catch (e: any) {
        if (active) setError(e?.message ?? "Nätverksfel vid hämtning av auktorisation.");
      }
    })();
    return () => { active = false; };
  }, [sessionReady, signedIn, authorizationId]);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setSigningIn(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSigningIn(false);
    if (error) setError(error.message);
  }

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const path = `/oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`;
      const payload: Record<string, unknown> = { action: approve ? "approve" : "deny" };
      if (approve && details?.scopes?.length) payload.approved_scopes = details.scopes;
      const { data, error } = await authFetch(path, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (error) { setBusy(false); return setError(error.message ?? "Åtgärden misslyckades."); }
      const target = data?.redirect_url ?? data?.redirect_to;
      if (!target) { setBusy(false); return setError("Ingen redirect returnerades."); }
      window.location.href = target;
    } catch (e: any) {
      setBusy(false);
      setError(e?.message ?? "Nätverksfel.");
    }
  }

  return (
    <main style={{ minHeight: "100dvh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Anslut till Korthet</CardTitle>
          <CardDescription>
            En extern klient (t.ex. ChatGPT eller Claude) vill koppla upp sig mot ditt Korthet-konto.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!sessionReady && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Laddar…</div>}
          {sessionReady && !signedIn && (
            <form onSubmit={signIn} className="space-y-4">
              <p className="text-sm text-muted-foreground">Logga in för att fortsätta.</p>
              <div className="space-y-2">
                <Label htmlFor="email">E-post</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Lösenord</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              </div>
              <Button type="submit" className="w-full" disabled={signingIn}>
                {signingIn && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Logga in
              </Button>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </form>
          )}
          {sessionReady && signedIn && !details && !error && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Hämtar begäran…</div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {details && (
            <div className="space-y-4">
              <div>
                <p className="text-sm">
                  <strong>{details.client?.name ?? "En extern klient"}</strong> vill komma åt Korthet som dig.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Detta ger klienten möjlighet att anropa Korthets MCP-verktyg med dina rättigheter. Admin-verktyg kräver att ditt konto har admin-roll.
                </p>
              </div>
              {details.redirect_uri && (
                <p className="text-xs text-muted-foreground">Callback: <code>{details.redirect_uri}</code></p>
              )}
              <div className="flex gap-2">
                <Button onClick={() => decide(true)} disabled={busy} className="flex-1">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Godkänn
                </Button>
                <Button onClick={() => decide(false)} disabled={busy} variant="outline" className="flex-1">Neka</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}